/**
 * Functional auth screens — Sprint 0 V4 Channel Atmosphere (frozen).
 * Behavior only; no visual redesign.
 * Customer accounts use Google Sign-In only — email register redirects to /login.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthChrome } from '../../components/brand/AuthChrome';
import { useLanguage } from '../../context/LanguageContext';
import { api } from '../../api/client';

export const RegisterPage: React.FC = () => {
  return <Navigate to="/login" replace />;
};

export const ForgotPasswordPage: React.FC = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [devToken, setDevToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setDevToken(null);
    setBusy(true);
    try {
      const result = await api.requestPasswordReset(email.trim());
      setSuccess(
        t(
          'auth.resetSent',
          'If the account exists, a reset link was sent. Check your email.'
        )
      );
      if (result.devResetToken) setDevToken(result.devResetToken);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.resetFailed', 'Request failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthChrome
      title={t('auth.forgotTitle', 'Reset your password')}
      subtitle={t('auth.forgotSubtitle', 'We will send a secure reset link to your email.')}
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        {error && (
          <p role="alert" className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2">
            {error}
          </p>
        )}
        {success && (
          <p role="status" className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-3 py-2">
            {success}
          </p>
        )}
        <div className="bc-field">
          <label className="bc-auth-label" htmlFor="forgot-email">
            {t('auth.email', 'Email')}
          </label>
          <input
            id="forgot-email"
            name="email"
            type="email"
            autoComplete="email"
            className="bc-auth-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder={t('auth.email', 'Email')}
          />
        </div>
        <button type="submit" className="bc-btn-primary w-full !py-3 !min-h-11" disabled={busy}>
          {busy ? t('auth.sending', 'Sending…') : t('auth.sendReset', 'Send reset link')}
        </button>
        {devToken && (
          <p className="text-xs text-zinc-500 break-all">
            <Link
              to={`/reset-password?email=${encodeURIComponent(email.trim())}&token=${encodeURIComponent(devToken)}`}
              className="text-brand-400 hover:text-brand-300"
            >
              {t('auth.devResetLink', 'Open reset link (local only)')}
            </Link>
          </p>
        )}
        <p className="text-xs text-zinc-500 text-center">
          <Link to="/login" className="text-brand-400 hover:text-brand-300">
            {t('auth.backToSignIn', 'Back to sign in')}
          </Link>
        </p>
      </form>
    </AuthChrome>
  );
};

export const ResetPasswordPage: React.FC = () => {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const emailParam = params.get('email') || '';
  const tokenParam = params.get('token') || '';
  const [email, setEmail] = useState(emailParam);
  const [token, setToken] = useState(tokenParam);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEmail(emailParam);
    setToken(tokenParam);
  }, [emailParam, tokenParam]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch', 'Passwords do not match.'));
      return;
    }
    setBusy(true);
    try {
      await api.confirmPasswordReset(email.trim(), token.trim(), password);
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.resetConfirmFailed', 'Reset failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthChrome
      title={t('auth.resetTitle', 'Choose a new password')}
      subtitle={t('auth.resetSubtitle', 'This link works once. After reset, sign in again on all devices.')}
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        {error && (
          <p role="alert" className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2">
            {error}
          </p>
        )}
        {success && (
          <p role="status" className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-3 py-2">
            {t('auth.resetSuccess', 'Password updated. Redirecting to sign in…')}
          </p>
        )}
        <div className="bc-field">
          <label className="bc-auth-label" htmlFor="reset-email">
            {t('auth.email', 'Email')}
          </label>
          <input
            id="reset-email"
            name="email"
            type="email"
            autoComplete="email"
            className="bc-auth-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        {!tokenParam && (
          <div className="bc-field">
            <label className="bc-auth-label" htmlFor="reset-token">
              {t('auth.resetToken', 'Reset token')}
            </label>
            <input
              id="reset-token"
              name="token"
              className="bc-auth-input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
              minLength={20}
            />
          </div>
        )}
        <div className="bc-field">
          <label className="bc-auth-label" htmlFor="reset-password">
            {t('auth.newPassword', 'New password')}
          </label>
          <input
            id="reset-password"
            name="password"
            type="password"
            autoComplete="new-password"
            className="bc-auth-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={10}
          />
        </div>
        <div className="bc-field">
          <label className="bc-auth-label" htmlFor="reset-confirm">
            {t('auth.confirmPassword', 'Confirm password')}
          </label>
          <input
            id="reset-confirm"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            className="bc-auth-input"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={10}
          />
        </div>
        <button type="submit" className="bc-btn-primary w-full !py-3 !min-h-11" disabled={busy || success}>
          {busy ? t('auth.saving', 'Saving…') : t('auth.savePassword', 'Save password')}
        </button>
        <p className="text-xs text-zinc-500 text-center">
          <Link to="/login" className="text-brand-400 hover:text-brand-300">
            {t('auth.backToSignIn', 'Back to sign in')}
          </Link>
        </p>
      </form>
    </AuthChrome>
  );
};

export const VerifyEmailPage: React.FC = () => {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const emailFromQuery = params.get('email') || '';
  const tokenFromQuery = params.get('token') || '';
  const [email, setEmail] = useState(emailFromQuery);
  const [status, setStatus] = useState<'idle' | 'verifying' | 'ok' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const autoToken = useMemo(() => tokenFromQuery, [tokenFromQuery]);

  useEffect(() => {
    if (!autoToken) return;
    let cancelled = false;
    setStatus('verifying');
    api
      .verifyEmail(autoToken)
      .then(() => {
        if (cancelled) return;
        setStatus('ok');
        setMessage(t('auth.verifySuccess', 'Email verified. You can sign in now.'));
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus('error');
        setMessage(err instanceof Error ? err.message : t('auth.verifyFailed', 'Verification failed.'));
      });
    return () => {
      cancelled = true;
    };
  }, [autoToken, t]);

  const resend = async () => {
    if (!email.trim()) {
      setMessage(t('auth.emailRequired', 'Enter your email to resend.'));
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.resendVerification(email.trim());
      if (result.alreadyVerified) {
        setMessage(t('auth.alreadyVerified', 'This email is already verified. You can sign in.'));
        setStatus('ok');
      } else {
        setMessage(t('auth.verifyResent', 'If an unverified account exists, a new email was sent.'));
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : t('auth.resendFailed', 'Could not resend.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthChrome
      title={t('auth.verifyTitle', 'Verify your email')}
      subtitle={t('auth.verifySubtitle', 'Confirm your inbox to finish setting up BeCoder.')}
    >
      <div className="space-y-5 text-center py-2">
        <div className="bc-auth-verify-orb" aria-hidden />
        <p className="text-sm text-zinc-400 leading-relaxed">
          {status === 'verifying'
            ? t('auth.verifying', 'Verifying your email…')
            : t(
                'auth.verifyBody',
                'Open the message from BeCoder and tap the verification link.'
              )}
        </p>
        {message && (
          <p
            role={status === 'error' ? 'alert' : 'status'}
            className={`text-sm rounded-md px-3 py-2 ${
              status === 'error'
                ? 'text-red-300 bg-red-500/10 border border-red-500/20'
                : 'text-emerald-300 bg-emerald-500/10 border border-emerald-500/20'
            }`}
          >
            {message}
          </p>
        )}
        <div className="bc-field text-start">
          <label className="bc-auth-label" htmlFor="verify-email">
            {t('auth.email', 'Email')}
          </label>
          <input
            id="verify-email"
            type="email"
            autoComplete="email"
            className="bc-auth-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.email', 'Email')}
          />
        </div>
        <button
          type="button"
          className="bc-btn-primary w-full !py-3 !min-h-11"
          disabled={busy || status === 'verifying'}
          onClick={resend}
        >
          {busy ? t('auth.sending', 'Sending…') : t('auth.resendVerify', 'Resend email')}
        </button>
        {status === 'ok' && (
          <button
            type="button"
            className="bc-btn-ghost w-full !py-3 !min-h-11"
            onClick={() => navigate('/login')}
          >
            {t('auth.continueToSignIn', 'Continue to sign in')}
          </button>
        )}
        <p className="text-xs text-zinc-500">
          <Link to="/login" className="text-brand-400 hover:text-brand-300">
            {t('auth.backToSignIn', 'Back to sign in')}
          </Link>
        </p>
      </div>
    </AuthChrome>
  );
};
